import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info, Users, Building2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ServiceCard from "@/components/ServiceCard";
import CTABand from "@/components/CTABand";
import api from "@/lib/api";

export default function Business() {
  const [cards, setCards] = useState([]);
  useEffect(() => {
    api.get("/public/business").then((r) => setCards(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <Seo title="Business Immigration | ApexPath"
        description="Practical immigration support for UK businesses, employers and international professionals — Skilled Worker, Health & Care Worker and business visitor guidance." />
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "Business Immigration" }]}
        eyebrow="For Businesses"
        title="Supporting Global Talent and" highlight="Business Growth"
        image="/images/biz-band.jpg"
        subtitle="We help UK businesses recruit and retain international talent, with clear and practical advice on work routes and business immigration matters." />

      <section className="bg-white py-16">
        <div className="container-x">
          <h2 className="mb-2 font-serif text-3xl font-semibold text-navy">Our Business Immigration Services</h2>
          <p className="mb-10 max-w-2xl text-base text-slate-600">
            Practical immigration support for UK businesses, employers and international professionals. We help clients
            understand their options, navigate immigration requirements and take the appropriate next steps.
          </p>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((s) => <ServiceCard key={s.id} service={s} />)}

            {/* Employee Dependants */}
            <Link to="/services/dependants" data-testid="business-card-employee-dependants"
              className="card-hover group flex flex-col rounded-xl border border-slate-200 bg-white p-6">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-navy text-gold"><Users size={20} /></div>
              <h3 className="font-serif text-xl font-semibold text-navy">Employee Dependants</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">Support for eligible partners and children of sponsored employees joining them in the UK.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">View service <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
            </Link>

            {/* Employer Enquiry & Triage */}
            <Link to="/book-consultation" data-testid="business-card-employer-enquiry"
              className="card-hover group flex flex-col rounded-xl border border-slate-200 bg-white p-6">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-navy text-gold"><Building2 size={20} /></div>
              <h3 className="font-serif text-xl font-semibold text-navy">Employer Immigration Enquiry</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">Tell us about your recruitment, sponsorship or immigration needs. Our team will review your enquiry and discuss the appropriate next steps with you.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">Discuss Your Requirements <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
            </Link>
          </div>

          <div className="mt-10 flex items-start gap-4 rounded-xl border border-navy/10 bg-cream p-7">
            <Info className="mt-0.5 shrink-0 text-navy" size={22} />
            <p className="text-sm leading-relaxed text-slate-600">
              Services are provided within ApexPath's current IAA authorisation. Complex sponsor licence and
              compliance matters may be referred appropriately. We do not market services outside our authorised scope.
            </p>
          </div>

          <div className="mt-8 rounded-xl bg-navy p-8 text-center">
            <h3 className="font-serif text-2xl font-semibold text-white">Grow Your Business with Confidence</h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-300">
              Speak to our team about your recruitment and sponsorship needs.
            </p>
            <Link to="/book-consultation" className="btn-gold mt-5">Discuss Your Requirements <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
